import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { isEmbeddedInApp, notifyAppItemAdded } from "src/utils/embedded-app";

interface CartDrawerContextType {
  isCartDrawerOpen: boolean;
  openCartDrawer: () => void;
  closeCartDrawer: () => void;
  toggleCartDrawer: () => void;
  checkoutFunction?: () => void;
  setCheckoutFunction: (fn?: () => void) => void;
}

const CartDrawerContext = createContext<CartDrawerContextType | undefined>(
  undefined
);

export const useCartDrawer = (): CartDrawerContextType => {
  const context = useContext(CartDrawerContext);
  if (!context) {
    throw new Error("useCartDrawer must be used within a CartDrawerProvider");
  }
  return context;
};

interface CartDrawerProviderProps {
  children: ReactNode;
}

export const CartDrawerProvider: React.FC<CartDrawerProviderProps> = ({
  children,
}) => {
  const [isCartDrawerOpen, setIsCartDrawerOpen] = useState(false);
  const [checkoutFunction, setCheckoutFunction] = useState<
    (() => void) | undefined
  >();

  /*
    Inside the app's editor WebView, the app owns the cart.

    Handled here rather than at each call site because there are eight of them —
    design, canvas, art, photography, the product store, the calendar — and a
    ninth would have quietly reintroduced the bug. Adding a card opened this
    drawer inside the WebView and then the app opened its own cart on top: the
    customer was asked the same question twice, by two different carts.

    The message is what tells the app the item landed; it closes the editor and
    shows its own cart. If the bridge is missing we are not actually in the app,
    so the drawer opens as it always did.
  */
  const openCartDrawer = useCallback(() => {
    if (isEmbeddedInApp() && notifyAppItemAdded()) return;
    setIsCartDrawerOpen(true);
  }, []);
  const closeCartDrawer = useCallback(() => setIsCartDrawerOpen(false), []);
  const toggleCartDrawer = useCallback(
    () => setIsCartDrawerOpen((prev) => !prev),
    []
  );

  // Memoize so the context value only changes when the drawer state or the
  // registered checkout function actually change — a fresh object every render
  // would re-render every consumer (e.g. the Header) on each parent render.
  const value = useMemo(
    () => ({
      isCartDrawerOpen,
      openCartDrawer,
      closeCartDrawer,
      toggleCartDrawer,
      checkoutFunction,
      setCheckoutFunction,
    }),
    [
      isCartDrawerOpen,
      openCartDrawer,
      closeCartDrawer,
      toggleCartDrawer,
      checkoutFunction,
    ]
  );

  return (
    <CartDrawerContext.Provider value={value}>
      {children}
    </CartDrawerContext.Provider>
  );
};
